import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import { SITE_URL } from '../config/seo';
import api from '../utils/api';
import { stripHtmlTags } from '../utils/htmlUtils';
import { Clock, Calendar, User, Share2, Search, Maximize2, X } from 'lucide-react';
import { showSuccess } from '../utils/toast';

const getImageUrl = (image) => {
  if (!image) return null;
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${backendBase}${image.startsWith('/') ? '' : '/'}${image}`;
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readingTime, setReadingTime] = useState(0);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    if (lightboxOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [lightboxOpen]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/blogs/${slug}`);
        setBlog(res.data);

        // Calculate reading time
        const text = stripHtmlTags(res.data.content || '');
        const words = text.trim().split(/\s+/).length;
        setReadingTime(Math.ceil(words / 200)); // ~200 words per minute

        // Fetch related blogs
        if (res.data) {
          fetchRelatedBlogs(res.data);
        }
      } catch (e) {
        if (e.response?.status === 404) setBlog(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const fetchRelatedBlogs = async (currentBlog) => {
    try {
      const params = {
        status: 'published',
        limit: 6,
      };

      // Prioritize same category
      if (currentBlog.contentCategory) {
        params.category = currentBlog.contentCategory.slug;
      }

      const res = await api.get('/blogs', { params });

      // Filter out current blog and get related ones
      let related = (res.data.blogs || []).filter(b => b._id !== currentBlog._id);

      // If we have tags, boost blogs with matching tags
      if (currentBlog.contentTags && currentBlog.contentTags.length > 0) {
        const currentTagIds = currentBlog.contentTags.map(t => t._id);
        related = related.sort((a, b) => {
          const aMatchingTags = (a.contentTags || []).filter(t => currentTagIds.includes(t._id)).length;
          const bMatchingTags = (b.contentTags || []).filter(t => currentTagIds.includes(t._id)).length;
          return bMatchingTags - aMatchingTags;
        });
      }

      setRelatedBlogs(related.slice(0, 3));
    } catch (e) {
      console.error('Error fetching related blogs:', e);
    }
  };



  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/blog?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blog.title,
        text: `Check out this article: ${blog.title}`,
        url: window.location.href,
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      showSuccess('Link copied to clipboard!');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
      <PageLoader message="Loading article..." />
    </div>
  );

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
          <Share2 className="w-10 h-10 text-gray-400" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Article Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md">The article you are looking for might have been removed or is temporarily unavailable.</p>
        <Link
          to="/blog"
          className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
        >
          Back to Blog
        </Link>
      </div>
    );
  }

  const imgUrl = blog.image ? getImageUrl(blog.image) : undefined;

  return (
    <>
      <SEO
        title={`${blog.title} – Blog`}
        description={blog.excerpt ? blog.excerpt.replace(/<[^>]*>/g, '').slice(0, 160) : undefined}
        image={imgUrl}
        canonical={`${SITE_URL}/blog/${blog.slug}`}
      />

      <div className="min-h-screen bg-white dark:bg-gray-950 font-outfit">

        {/* Article Header (Smayjak Layout) */}
        <div className="smayjak-article-header">
          <div className="smayjak-header-inner">
            <div className="smayjak-breadcrumb flex items-center justify-center gap-2 flex-wrap">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/blog">Blog</Link>
              {blog.contentCategory && (
                <>
                  <span>/</span>
                  <Link to={`/blog/category/${blog.contentCategory.slug}`}>
                    {blog.contentCategory.name}
                  </Link>
                </>
              )}
            </div>

            <h1 className="smayjak-title">
              {blog.title}
            </h1>

            <div className="smayjak-meta-bar">
              <span className="smayjak-meta-item">
                <Calendar className="w-4 h-4 text-[#ea580c]" />
                {blog.publishedAt && new Date(blog.publishedAt).toLocaleDateString('en-IN', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
              <span>•</span>
              <span className="smayjak-meta-item">
                <Clock className="w-4 h-4 text-[#ea580c]" />
                {readingTime} min read
              </span>
              {blog.author && (
                <>
                  <span>•</span>
                  <span className="smayjak-meta-item">
                    <User className="w-4 h-4 text-[#ea580c]" />
                    {blog.author}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Article Reading Container */}
        <article className="smayjak-article-container">

          {/* Featured Image */}
          {imgUrl && (
            <div className="smayjak-banner-wrap group">
              <img
                src={imgUrl}
                alt={blog.title}
                className="cursor-pointer transition-transform duration-300 group-hover:scale-[1.01]"
                onClick={() => setLightboxOpen(true)}
              />
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="absolute bottom-4 right-4 bg-black/70 hover:bg-black/90 text-white px-3.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 backdrop-blur-md opacity-90 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-lg"
                title="View Full Size Image"
              >
                <Maximize2 className="w-3.5 h-3.5" /> Full View
              </button>
            </div>
          )}

          {/* Article Rich HTML Content */}
          <div
            className="smayjak-article-body"
            dangerouslySetInnerHTML={{ __html: blog.content || '' }}
          />

          {/* Social Share Bar */}
          <div className="smayjak-share-bar">
            <div className="text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2">
              <Share2 className="w-4 h-4 text-[#ea580c]" /> Share this article:
            </div>
            <div className="smayjak-share-btns">
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(blog.title + ' ' + (typeof window !== 'undefined' ? window.location.href : ''))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="smayjak-share-btn smayjak-share-wa"
                title="Share on WhatsApp"
              >
                WhatsApp
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="smayjak-share-btn smayjak-share-fb"
                title="Share on Facebook"
              >
                Facebook
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="smayjak-share-btn smayjak-share-x"
                title="Share on X"
              >
                X / Twitter
              </a>
              <button
                type="button"
                onClick={handleShare}
                className="smayjak-share-btn smayjak-share-copy"
                title="Copy Link"
              >
                Copy Link
              </button>
            </div>
          </div>

          {/* Tags Pills */}
          {blog.contentTags && blog.contentTags.length > 0 && (
            <div className="smayjak-tags-wrap">
              {blog.contentTags.map((t) => (
                <Link
                  key={t._id}
                  to={`/blog/tag/${t.slug}`}
                  className="smayjak-tag"
                >
                  #{t.name}
                </Link>
              ))}
            </div>
          )}

          {/* Author Box */}
          <div className="smayjak-author-box">
            <div className="smayjak-author-avatar">
              {blog.author ? blog.author.charAt(0).toUpperCase() : 'A'}
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900 dark:text-white m-0">
                {blog.author || 'Editorial Desk'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 mb-0 leading-relaxed">
                Curating the latest verified news, local updates, cultural articles, and business insights from Fatehabad and beyond.
              </p>
            </div>
          </div>

        </article>

        {/* Related Articles Grid */}
        {relatedBlogs.length > 0 && (
          <div className="smayjak-article-container pt-0 pb-16">
            <div className="border-t border-[#f1e1cf] dark:border-gray-800 pt-10">
              <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-6">
                Related Articles
              </h3>
              <div className="smayjak-related-grid">
                {relatedBlogs.map((relatedBlog) => (
                  <Link
                    key={relatedBlog._id}
                    to={`/blog/${relatedBlog.slug}`}
                    className="smayjak-related-card"
                  >
                    {relatedBlog.image && (
                      <img
                        src={getImageUrl(relatedBlog.image)}
                        alt={relatedBlog.title}
                      />
                    )}
                    <div className="smayjak-related-body">
                      {relatedBlog.contentCategory && (
                        <span className="text-xs font-bold text-[#ea580c] uppercase tracking-wider mb-1.5 inline-block">
                          {relatedBlog.contentCategory.name}
                        </span>
                      )}
                      <h4 className="smayjak-related-title">
                        {relatedBlog.title}
                      </h4>
                      {relatedBlog.excerpt && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                          {stripHtmlTags(relatedBlog.excerpt)}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Explore & Search Bar */}
        <div className="max-w-[820px] mx-auto px-4 pb-20">
          <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-amber-600/5 dark:from-gray-900 dark:to-gray-800 rounded-2xl p-6 sm:p-8 border border-amber-200/60 dark:border-gray-800 text-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Explore More Articles</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Search through thousands of business updates, local announcements, and stories.
            </p>
            <form onSubmit={handleSearch} className="relative max-w-lg mx-auto">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search articles, topics, businesses..."
                className="w-full px-5 py-3 pr-12 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ea580c] focus:border-transparent transition-all shadow-sm"
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-[#ea580c] transition-colors"
              >
                <Search className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>

      </div>

      {/* Full Size Image Lightbox Modal */}
      {lightboxOpen && imgUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-2 sm:p-6"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-10 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            title="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="relative max-w-[95vw] max-h-[92vh] flex items-center justify-center">
            <img
              src={imgUrl}
              alt={blog.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

    </>
  );
};

export default BlogDetailPage;
